
import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { BookOpen } from "lucide-react";
import TextSection from "./components/TextSection";
import ScriptureSection from "./components/ScriptureSection";
import ObjectionsSection from "./components/ObjectionsSection";
import ResponseActions from "./ResponseActions";
import type { DefenseResponse } from "./types";

interface ResponseDisplayProps {
  response: DefenseResponse;
  isSaving?: boolean;
  onSave?: () => void;
  onShareEmail?: () => void;
  onShareSMS?: () => void;
  onShareFacebook?: () => void;
}

const ResponseDisplay: React.FC<ResponseDisplayProps> = ({ 
  response,
  isSaving = false,
  onSave = () => {},
  onShareEmail = () => {},
  onShareSMS = () => {},
  onShareFacebook = () => {}
}) => {
  // Ensure we have a response object
  if (!response) {
    return (
      <Card className="border-heaven-gold/10 mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-heaven-gold">
            <BookOpen className="h-5 w-5" />
            Error: No Response
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p>No response data was received. Please try your question again.</p>
        </CardContent>
      </Card>
    );
  }

  // Adapter function to handle potentially missing fields with graceful defaults
  const adaptResponse = (data: any): DefenseResponse => {
    return {
      directAnswer: typeof data.directAnswer === 'string' ? data.directAnswer : "",
      biblicalFoundation: typeof data.biblicalFoundation === 'string' ? data.biblicalFoundation : "",
      scriptureReferences: Array.isArray(data.scriptureReferences) ? data.scriptureReferences : [],
      theologicalReasoning: typeof data.theologicalReasoning === 'string' ? data.theologicalReasoning : "",
      historicalContext: typeof data.historicalContext === 'string' ? data.historicalContext : "",
      objections: typeof data.objections === 'string' ? data.objections : "",
      finalEncouragement: typeof data.finalEncouragement === 'string' ? data.finalEncouragement : ""
    };
  };

  // Apply adapter to ensure consistent structure
  const adaptedResponse = adaptResponse(response);
  
  // Clean the final encouragement to prevent duplicated content
  const cleanFinalEncouragement = adaptedResponse.finalEncouragement
    ? cleanDuplicatedParagraphs(adaptedResponse.finalEncouragement)
    : "";
  
  // Clean objections to prevent duplicated content
  const cleanObjections = adaptedResponse.objections
    ? cleanDuplicatedParagraphs(adaptedResponse.objections)
    : "";
  
  // Determine if we should use the structured objections component
  const hasStructuredObjections = 
    cleanObjections && 
    (cleanObjections.includes("Objection 1:") || 
     cleanObjections.includes("Objection:") ||
     cleanObjections.includes("Response:"));

  return (
    <Card className="border-heaven-gold/10 mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-heaven-gold">
          <BookOpen className="h-5 w-5" />
          Biblical Answer
        </CardTitle>
      </CardHeader>

      {/* Add ResponseActions at the top */}
      <div className="px-6">
        <ResponseActions
          onSave={onSave}
          onShareEmail={onShareEmail}
          onShareSMS={onShareSMS}
          onShareFacebook={onShareFacebook}
          isSaving={isSaving}
        />
      </div>
      
      <CardContent className="space-y-8">
        {/* Direct Answer Section - only show if we have content */}
        {adaptedResponse.directAnswer && (
          <TextSection 
            number={1} 
            title="Direct Answer" 
            content={adaptedResponse.directAnswer} 
          />
        )}
        
        {/* Biblical Foundation Section - only show if we have content */}
        {adaptedResponse.biblicalFoundation && (
          <TextSection 
            number={2} 
            title="Biblical Foundation" 
            content={adaptedResponse.biblicalFoundation} 
          />
        )}
        
        {/* Scripture References Section - only show if we have references */}
        {adaptedResponse.scriptureReferences && adaptedResponse.scriptureReferences.length > 0 && (
          <ScriptureSection scriptureReferences={adaptedResponse.scriptureReferences} />
        )}
        
        {/* Theological Reasoning Section - only show if we have content */}
        {adaptedResponse.theologicalReasoning && (
          <TextSection 
            number={4} 
            title="Logical, Philosophical, and Moral Reasoning" 
            content={adaptedResponse.theologicalReasoning} 
          />
        )}
        
        {/* Historical Context Section - only show if we have content */}
        {adaptedResponse.historicalContext && (
          <TextSection 
            number={5} 
            title="Historical and Scientific Support" 
            content={adaptedResponse.historicalContext} 
          />
        )}
        
        {/* Common Objections Section - only show if we have content */}
        {cleanObjections && (
          hasStructuredObjections ? (
            <ObjectionsSection objections={cleanObjections} />
          ) : (
            <TextSection 
              number={6} 
              title="Addressing Common Objections" 
              content={cleanObjections} 
            />
          )
        )}
        
        {/* Final Encouragement Section - only show if we have content */}
        {cleanFinalEncouragement && (
          <TextSection 
            number={7} 
            title="Final Encouragement" 
            content={cleanFinalEncouragement} 
            isLast={true}
          />
        )}
      </CardContent>
      
      {/* Add ResponseActions at the bottom too */}
      <div className="px-6 pb-6">
        <ResponseActions
          onSave={onSave}
          onShareEmail={onShareEmail}
          onShareSMS={onShareSMS}
          onShareFacebook={onShareFacebook}
          isSaving={isSaving}
        />
      </div>
    </Card>
  );
};

/**
 * Remove duplicate paragraphs from text content with enhanced detection
 */
function cleanDuplicatedParagraphs(text: string): string {
  if (!text) return '';
  
  // Check for section duplication first (common in objections)
  const sectionMarkers = text.match(/(?:Objection\s+\d+:|Objection:|Response:|Final Encouragement:)/gi) || [];
  const uniqueMarkers = [...new Set(sectionMarkers.map(m => m.toLowerCase()))];
  
  // If we detect duplicate markers, try to clean up the text
  if (sectionMarkers.length > uniqueMarkers.length) {
    // Try to find where the first complete set ends
    const objectionMatches = Array.from(text.matchAll(/Objection\s+(\d+):|Objection:/gi));
    if (objectionMatches.length >= 2) {
      for (let i = 1; i < objectionMatches.length; i++) {
        // Look for repeated patterns
        const currentMatch = objectionMatches[i];
        if (currentMatch[1]) {
          const currentNum = parseInt(currentMatch[1], 10);
          for (let j = 0; j < i; j++) {
            const prevNum = objectionMatches[j][1] ? parseInt(objectionMatches[j][1], 10) : 0;
            if (currentNum === prevNum && currentMatch.index) {
              // Found a repeat, return content up to this point
              return text.substring(0, currentMatch.index).trim();
            }
          }
        }
      }
    }
  }
  
  // If no clear section duplication, use paragraph-level deduplication
  const paragraphs = text.split(/\n+/).filter(Boolean);
  const uniqueParagraphs = [];
  const seenParagraphs = new Set();
  
  for (const paragraph of paragraphs) {
    // Create comprehensive fingerprint for deduplication
    const normalized = paragraph.toLowerCase()
      .replace(/\d+/g, '')  // Remove numbers
      .replace(/[^\w\s]/g, '')  // Remove punctuation
      .replace(/\s+/g, ' ')  // Normalize whitespace
      .trim();
    
    if (!seenParagraphs.has(normalized) && normalized.length > 0) {
      seenParagraphs.add(normalized);
      uniqueParagraphs.push(paragraph);
    }
  }
  
  return uniqueParagraphs.join('\n\n');
}

export default ResponseDisplay;
